import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { DataTable, type Column } from '@/components/shared/DataTable';
import { useCurrency } from '@/hooks/useCurrency';
import { useWeightUnit } from '@/hooks/useWeightUnit';
import { CARD, Notice, OccupancyBar } from '@/modules/supply/components/LoadEstimateParts';
import type { LoadEstimate } from '@/modules/supply/hooks/useLoadEstimate';
import { useNumberFormats } from '@/modules/supply/hooks/useNumberFormats';
import type { LoadEstimateSupplierResult } from '@/modules/supply/utils/loadEstimate';
import { LOAD_ESTIMATE_KEY as T, containerCodeOf } from '@/modules/supply/utils/loadEstimateDraft';
import { supplierColor } from '@/modules/supply/utils/loadColors';

/** Tabla de proveedores validados: cifras de cada estimación, selección de los que entran en la orden y buscador. */
export function LoadEstimateSuppliersTable({ estimate, activeSourceId }: { estimate: LoadEstimate; activeSourceId: number | null }) {
  const { t } = useTranslation();
  const { number, decimal } = useNumberFormats();
  const { formatRounded } = useCurrency();
  const { unit: weightUnit, fromKg } = useWeightUnit();
  const [search, setSearch] = useState('');
  const {
    awaiting, rowIds, tableRows, marked, setMarked, toggleMarked, addSupplier, removeSupplier, requestedSupplierIds, templates,
    calculating, changeContainer, setActiveSupplierId, loading, pendingLoading,
  } = estimate;

  const markableRows = tableRows.filter((row) => row.source);
  const allMarked = markableRows.length > 0 && markableRows.every((row) => marked.includes(row.supplier_id));

  const matches = search.trim().length > 0
    ? awaiting
      .filter((row) => !rowIds.includes(row.supplier_id) && row.supplier_name.toLowerCase().includes(search.trim().toLowerCase()))
      .slice(0, 8)
    : [];

  const columns: Column[] = [
    {
      header: (
        <input
          type="checkbox"
          aria-label={t(`${T}.selectAllRows`)}
          title={t(`${T}.selectAllRows`)}
          className="h-4 w-4 rounded border-gray-300 text-[#1681ba] focus:ring-[#1681ba]"
          checked={allMarked}
          disabled={markableRows.length === 0}
          onClick={(event) => event.stopPropagation()}
          onChange={(event) => setMarked(event.target.checked ? markableRows.map((row) => row.supplier_id) : [])}
        />
      ),
      accessor: 'select',
      width: '40px',
      cell: (_value, row) => (
        <input
          type="checkbox"
          aria-label={row.supplier_name}
          className="h-4 w-4 rounded border-gray-300 text-[#1681ba] focus:ring-[#1681ba]"
          checked={marked.includes(row.supplier_id)}
          disabled={!row.source}
          onClick={(event) => event.stopPropagation()}
          onChange={() => toggleMarked(row.supplier_id)}
        />
      ),
    },
    {
      header: t(`${T}.columnSupplier`),
      accessor: 'supplier_name',
      sortable: true,
      cell: (value, row) => (
        <span className="font-medium text-gray-900">
          <span className="mr-2 inline-block h-2.5 w-2.5 rounded-full align-middle" style={{ backgroundColor: supplierColor(row.supplier_id) }} aria-hidden="true" />
          {value}
          {requestedSupplierIds.includes(row.supplier_id) ? (
            <span className="ml-2 rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-medium text-[#1681ba]">{t(`${T}.requestedBadge`)}</span>
          ) : null}
          {row.kind === 'suggested' ? (
            <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-700">{t(`${T}.suggestedBadge`)}</span>
          ) : null}
        </span>
      ),
    },
    {
      header: t(`${T}.columnContainer`),
      accessor: 'container',
      cell: (_value, row) => {
        const source: LoadEstimateSupplierResult | undefined = row.source;
        if (!source) return <span className="text-xs text-gray-500">{t(`${T}.calculatingRow`)}</span>;
        if (source.unavailableReason) {
          return <span className="text-amber-700" title={source.unavailableReason}>{t(`${T}.notEstimable`)}</span>;
        }
        const currentCode = containerCodeOf(source);
        const currentTemplate = templates.find((item) => item.code === currentCode);
        return (
          <select
            aria-label={`${t(`${T}.columnContainer`)} · ${source.supplierName}`}
            value={currentTemplate ? String(currentTemplate.id) : ''}
            disabled={calculating}
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => { if (event.target.value) void changeContainer(source.supplierId, Number(event.target.value)); }}
            className="max-w-52 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs focus:border-[#1681ba] focus:outline-none focus:ring-1 focus:ring-[#1681ba]"
          >
            {currentTemplate ? null : <option value="">{currentCode || '—'}</option>}
            {templates.map((template) => (
              <option key={template.id} value={template.id}>
                {template.code}{template.usable_volume_m3 ? ` · ${number.format(template.usable_volume_m3)} m³` : ''}
              </option>
            ))}
          </select>
        );
      },
    },
    { header: t(`${T}.columnProducts`), accessor: 'products', sortable: true, align: 'right', numeric: true },
    { header: t(`${T}.columnAmount`), accessor: 'amount', sortable: true, align: 'right', cell: (value) => formatRounded(value) },
    { header: t(`${T}.columnVolume`), accessor: 'volume', sortable: true, align: 'right', cell: (value) => (value == null ? '—' : `${decimal.format(value)} m³`) },
    { header: t(`${T}.columnVolumePct`), accessor: 'volumePct', sortable: true, cell: (value) => <OccupancyBar pct={value} /> },
    { header: t(`${T}.columnWeight`), accessor: 'weight', sortable: true, align: 'right', cell: (value) => (value == null ? '—' : `${number.format(fromKg(value))} ${weightUnit}`) },
    { header: t(`${T}.columnWeightPct`), accessor: 'weightPct', sortable: true, cell: (value) => <OccupancyBar pct={value} /> },
    {
      header: '',
      accessor: 'remove',
      width: '36px',
      cell: (_value, row) => (row.kind === 'suggested' ? (
        <button
          type="button"
          aria-label={t(`${T}.removeSupplier`)}
          title={t(`${T}.removeSupplier`)}
          onClick={(event) => { event.stopPropagation(); removeSupplier(row.supplier_id); }}
          className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-red-700"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      ) : null),
    },
  ];

  return (
      <section className={`${CARD} p-4`}>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-base font-semibold text-gray-900">
            {t(`${T}.validatedTitle`)}
            {tableRows.length > 0 ? <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">{tableRows.length}</span> : null}
          </h2>
          <div className="relative w-full sm:w-80">
            <label className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm focus-within:border-[#1681ba] focus-within:ring-1 focus-within:ring-[#1681ba]">
              <Search className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
              <span className="sr-only">{t(`${T}.searchSupplier`)}</span>
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t(`${T}.searchSupplier`)}
                className="w-full bg-transparent text-sm outline-none placeholder:text-gray-400"
              />
            </label>
            {search.trim().length > 0 ? (
              <ul className="absolute right-0 z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg" role="listbox">
                {matches.length === 0 ? (
                  <li className="px-3 py-2 text-xs text-gray-500">{t(`${T}.searchNoMatches`)}</li>
                ) : matches.map((row) => (
                  <li key={row.supplier_id}>
                    <button type="button" onClick={() => { addSupplier(row.supplier_id); setSearch(''); }} className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-gray-50">
                      <span className="font-medium text-gray-900">{row.supplier_name}</span>
                      <span className="shrink-0 text-xs text-gray-500">{t(`${T}.productsCount`, { count: row.products })}</span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>
        {loading ? <p className="py-6 text-xs text-gray-500">{t(`${T}.loading`)}</p> : null}
        {!pendingLoading && tableRows.length === 0 ? (
          <Notice tone="info">
            <span>{t(`${T}.emptyValidated`)}</span>
            <Link to="/supply" className="font-semibold underline">{t(`${T}.goToSupply`)}</Link>
          </Notice>
        ) : null}
        {tableRows.length > 0 ? (
          <DataTable
            columns={columns}
            data={tableRows}
            compact
            pagination={false}
            onRowClick={(row) => { if (row.source && !row.source.unavailableReason) setActiveSupplierId(row.supplier_id); }}
            rowClassName={(row) => (activeSourceId === row.supplier_id ? 'bg-blue-50' : '')}
          />
        ) : null}
      </section>
  );
}
